<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';

  import { EditForm } from '@client/pages/marketing/campaigns/manage/edit-form';

  import { AssetsService } from '@client/services/assets';
  import { CampaignsService } from '@client/services/campaigns';
  import { TargetingService } from '@client/services/targeting';

  import { appStore } from '@client/stores/app';
  import { defaultCampaignTags } from '@client/stores/domain-settings';

  import { determineCampaignStatus } from '@client/utils/campaigns';
  import { structureTree } from '@client/utils/folder-tree';
  import { sanitizeTagValue } from '@client/utils/sanitize';

  import { AD_TYPES, CAMPAIGN_CATEGORIES, CAMPAIGN_CATEGORIES_ARRAY, LINK_TYPES } from '@client/enums';

  import { deepCopyArray } from '@common/utils/arrays';
  import { formatDate, isValidDate } from '@common/utils/dates';
  import { deepCopy } from '@common/utils/objects';
  import { isValidUrl } from '@common/utils/validation';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let currentStep = 0;
  let maxStepReached = 0;

  // Campaign selection state
  let campaigns = [];
  let selectedCampaign = null;
  let loading = true;
  let imageLoading = false;

  // Edit form state
  let campaign = null;
  let originalCampaign = null;

  // Schedule state
  let startDate = '';
  let endDate = '';
  let isOpen = false;
  let defaultMonth = new Date().getMonth();
  let defaultYear = new Date().getFullYear();
  let dateInputValue = '';
  let dateFormat = 'EEE, MMM d, yy @ h:mm a';

  // Tags state
  let tags = [];
  let selectedTags = [];
  let filterText = '';

  // Categories state
  let categories = deepCopyArray(CAMPAIGN_CATEGORIES_ARRAY);
  let selectedCategories = [];

  // Folders state
  let folders = [];
  let selectedFolders = [];
  let selectedLabels = [];

  // Assets state
  let documentItems = [];
  let selectedDocument = '';
  let videoItems = [];
  let selectedVideo = '';

  // Files state
  let files = [];
  let fileData = {};

  // Validation state
  let validationError = false;
  let errorMessage = '';

  // Targeting state
  let activeTargeting = {};
  let recipients = [];
  let domains = [];
  let originalRecipients = [];
  let originalDomains = [];

  const loadCampaigns = async () => {
    loading = true;
    try {
      const allCampaigns = await CampaignsService.getAll();
      campaigns = allCampaigns.map((c) => ({
        label: c.rulename,
        value: c.rulename,
        ...c
      }));
    } catch (error) {
      notify($format('label.FAILURE.LOAD_CAMPAIGNS'), 'error');
    } finally {
      loading = false;
    }
  };

  const onSelectCampaign = async (event) => {
    const campaignData = event.detail;

    if (!campaignData) {
      selectedCampaign = null;
      campaign = null;
      originalCampaign = null;
      return;
    }

    selectedCampaign = campaignData;
    campaign = deepCopy(campaignData);

    if (campaign.adtype === 'image') {
      imageLoading = true;
    }

    // Setup all the campaign data
    await setupCampaign();

    // Store original after setup
    originalCampaign = deepCopy(campaign);
    originalRecipients = deepCopyArray(recipients);
    originalDomains = deepCopyArray(domains);
  };

  const onImageLoad = () => {
    imageLoading = false;
  };

  const onImageError = () => {
    imageLoading = false;
  };

  // Setup functions
  const setupTargeting = async () => {
    activeTargeting = await TargetingService.get();

    recipients = [];
    domains = [];

    campaign = determineCampaignStatus(campaign, activeTargeting);

    activeTargeting.accounts
      .filter(({ ads }) => ads.filter(({ text }) => text === campaign.rulename).length)
      .forEach(({ name }) => {
        if (name.indexOf('@') > -1) {
          recipients.push(name);
        } else {
          domains.push(name);
        }
      });

    recipients = [...recipients];
    domains = [...domains];
  };

  const setupAssetFolderTree = async () => {
    const response = await AssetsService.getAssetFolders();

    if (!response.length) {
      folders = [];
      return;
    }

    folders = [structureTree(response)];

    if (campaign?.folderid) {
      setDefaultFolderSelection(campaign?.folderid, folders);
    }
  };

  const setDefaultFolderSelection = (id, fldrs) => {
    for (const folder of fldrs) {
      if (folder.id === id) {
        onFolderSelection(folder);
      }

      if (folder.files?.length) {
        setDefaultFolderSelection(id, folder.files);
      }
    }
  };

  const setupAssets = async () => {
    await setupAssetFolderTree();

    const documentList = await CampaignsService.getDocumentList();
    const videoList = await CampaignsService.getVideoList();

    documentItems = documentList.map(({ rulename, ...rest }) => ({
      label: rulename,
      value: rulename,
      rulename,
      ...rest
    }));

    videoItems = videoList.map(({ rulename, ...rest }) => ({
      label: rulename,
      value: rulename,
      rulename,
      ...rest
    }));

    selectedDocument = '';
    selectedVideo = '';
    files = [];
    fileData = {};

    if (campaign?.linktype === LINK_TYPES.doc.id && campaign.docinfo) {
      selectedDocument = documentItems.find((item) => item.value === campaign.docinfo.rulename);
    }

    if (campaign?.linktype === LINK_TYPES.video.id && campaign.videoinfo) {
      selectedVideo = videoItems.find((item) => item.value === campaign.videoinfo.rulename);
    }
  };

  const setupCategories = () => {
    selectedCategories = Object.keys(campaign.category || {}).map((key) => ({
      label: $format(CAMPAIGN_CATEGORIES[key]?.label) || key,
      value: key
    }));
  };

  const setupTags = () => {
    selectedTags = (campaign.tags || []).map(({ text }) => text);

    const targetingTags = activeTargeting?.departments?.map(({ tags }) => tags.map(({ text }) => text)).flat() || [];

    tags = Array.from(
      new Map(
        [...($defaultCampaignTags || []), ...(targetingTags || []), ...selectedTags].sort().map((text) => [
          text,
          {
            label: text,
            value: text,
            checked: selectedTags.includes(text)
          }
        ])
      ).values()
    );
  };

  const setupCampaign = async () => {
    await setupTargeting();
    await setupAssets();

    setupCategories();
    setupTags();

    if (campaign?.schedule?.startDate) {
      startDate = new Date(campaign.schedule.startDate);
      endDate = new Date(campaign.schedule.endDate);
    }

    campaign = { ...campaign };
  };

  // Event handlers
  const toggleDatePicker = () => {
    return startDate && !endDate ? (isOpen = true) : (isOpen = !isOpen);
  };

  const formatDateString = (dateString) => {
    if (!dateString) return '';
    return formatDate(new Date(dateString), dateFormat);
  };

  const onDatePickerChange = (dateRange = {}) => {
    if (dateRange.startDate && dateRange.endDate) {
      const dateStart = new Date(dateRange.startDate);
      const dateEnd = new Date(dateRange.endDate);

      if (isValidDate(dateRange.startDate) && isValidDate(dateRange.endDate)) {
        if (dateStart !== startDate) startDate = dateStart;
        if (dateEnd !== endDate) endDate = dateEnd;

        campaign.schedule = { startDate, endDate };
      }
    }
  };

  const onDateInputChange = () => {
    const splitDates = dateInputValue.split(' - ');
    const [start, end] = splitDates;

    if (start && end) {
      const dateStart = new Date(start);
      const dateEnd = new Date(end);

      if (isValidDate(dateStart) && isValidDate(dateEnd)) {
        if (dateStart !== startDate) startDate = dateStart;
        if (dateEnd !== endDate) endDate = dateEnd;
        campaign.schedule = { startDate, endDate };
      }
    } else {
      campaign.schedule = null;
      startDate = '';
      endDate = '';
    }
  };

  const onTagChange = (e) => {
    const currentTags = tags || [];
    filterText = sanitizeTagValue(filterText);

    if (!selectedTags) {
      selectedTags = [];
      campaign.tags = [];
      return;
    }

    if (e.key === 'Enter' || e.type === 'blur') {
      if (selectedTags.map(({ value }) => value).includes(filterText)) {
        filterText = '';
        return;
      }

      if (!currentTags.includes(filterText) && filterText.length) {
        selectedTags = [{ label: filterText, value: filterText }, ...selectedTags];
        filterText = '';
      }
    } else {
      if (!filterText.length && e.detail?.value) {
        const selected = e.detail.value;
        if (!currentTags.includes(selected) && selected.length) {
          selectedTags = [selected, ...selectedTags];
        }
      }
    }

    campaign.tags = selectedTags.map((tag) => ({ text: tag.value }));
  };

  const onTagClear = (e) => {
    if (!selectedTags) {
      selectedTags = [];
      campaign.tags = [];
      return;
    }

    selectedTags = selectedTags.filter(({ value }) => value !== e.detail.value);
    campaign.tags = selectedTags.map(({ value }) => ({ text: value }));
  };

  const onCategoryChange = () => {
    let categoriesObject = {};

    if (!selectedCategories) {
      selectedCategories = [];
      campaign.category = {};
      return;
    }

    selectedCategories = selectedCategories.map((category) => {
      categoriesObject[category.value] = true;
      return { ...category, label: $format(category.label), checked: true };
    });

    campaign.category = categoriesObject;
  };

  const resetFolderSelections = () => {
    selectedFolders = [];
    selectedLabels = [];
  };

  const updateFolderSelections = (id, name, override) => {
    const exists = override ?? selectedFolders.includes(id);

    if (exists) {
      if (selectedFolders.includes(id)) {
        selectedFolders.splice(selectedFolders.indexOf(id), 1);
        selectedLabels.splice(selectedLabels.indexOf(name), 1);
      }
    } else {
      resetFolderSelections();
      if (!selectedFolders.includes(id)) {
        selectedFolders.push(id);
        selectedLabels.push(name);
      }
    }

    selectedFolders = [...selectedFolders];
    selectedLabels = [...selectedLabels];
    campaign.folderid = selectedFolders.length ? selectedFolders[0] : null;
  };

  const onFolderSelection = ({ id, name }, override) => {
    updateFolderSelections(id, name, override);
  };

  const onSelect = (e, file) => {
    e.preventDefault();
    e.stopPropagation();
    onFolderSelection(file);
  };

  const onClickAction = (type) => {
    campaign.params = [];
    campaign.linktype = type;

    if (type === LINK_TYPES.url.id) {
      onUrlChange();
    }
  };

  const onUrlChange = () => {
    if (!campaign.url?.length) {
      campaign.params = [];
    }

    if (campaign.url?.length && !validationError) {
      try {
        const urlObj = new URL(campaign?.url);
        const searchParams = new URLSearchParams(urlObj.search);

        campaign.params = [];

        if (searchParams.size > 0) {
          searchParams.forEach((value, name) => {
            const findIndex = campaign.params.findIndex((param) => param.name === name);
            if (findIndex === -1) {
              campaign.params.push({ name, value });
            } else {
              campaign.params[findIndex].value = value;
            }
          });

          campaign.params = [...campaign.params];
        }
      } catch {
        // Invalid URL
      }
    }
  };

  const onParamsChange = () => {
    const hasParams = campaign.params.length;
    const hasQueryParams = campaign.url.indexOf('?') > -1;

    let url = campaign.url.slice(
      0,
      hasQueryParams ? campaign.url.indexOf('?') + (hasParams ? 1 : 0) : campaign.url.length
    );

    if (hasParams) {
      const pairs = campaign.params.map(({ name, value }) => `${name}=${value}`);
      url += hasQueryParams ? pairs.join('&') : `?${pairs.join('&')}`;
    }

    campaign.url = url;
  };

  const onDocumentChange = () => {
    campaign.docinfo = { ...selectedDocument };
    campaign.docname = selectedDocument?.rulename;
  };

  const onVideoChange = () => {
    campaign.videoinfo = { ...selectedVideo };
    campaign.videoname = selectedVideo?.rulename;
  };

  const onAdType = (e) => {
    files = [];
    fileData = {};
    campaign.adtype = e.target.innerText.toLowerCase();
  };

  const onFileUpload = async () => {
    const file = new FormData();
    file.append('file', files[0]);

    const response = await AssetsService.uploadFile(files[0].name, files[0].type, file);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: files[0].name } }), 'error');
      return;
    }

    const { hash, filesize, filename, metadata } = response;

    fileData = { ...fileData, hash, filesize, filename, metadata };

    campaign = {
      ...campaign,
      filename: fileData?.filename || campaign?.filename,
      filesize: fileData?.filesize || campaign?.filesize,
      hash: fileData?.hash || campaign?.hash,
      metadata: fileData?.metadata || campaign?.metadata,
      mimetype: fileData?.metadata?.mimetype || campaign?.mimetype
    };
  };

  const onRemoveFile = () => {
    files = [];
    fileData = {};
  };

  const onValidation = () => {
    validationError = !isValidUrl(campaign.url);

    if (validationError) {
      errorMessage = $format('label.VALIDATION_ERROR_NO_VALID_URL_CAMPAIGNS');
    }
  };

  // Step handlers
  const handleNext = () => {
    if (selectedCampaign && campaign) {
      currentStep = 1;
      if (currentStep > maxStepReached) {
        maxStepReached = currentStep;
      }
    }
  };

  const handleBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentStep = stepIndex - 1;
    }
  };

  // Execute and rollback
  const executeUpdate = async () => {
    $appStore.loading = true;

    try {
      // Clean up campaign based on link type
      if (campaign.linktype === LINK_TYPES.url.id) {
        campaign.videoinfo = null;
        campaign.videoname = null;
        campaign.docinfo = null;
        campaign.docname = null;
      }

      if (campaign.linktype === LINK_TYPES.doc.id) {
        campaign.url = null;
        campaign.videoinfo = null;
        campaign.videoname = null;
      }

      if (campaign.linktype === LINK_TYPES.video.id) {
        campaign.url = null;
        campaign.docinfo = null;
        campaign.docname = null;
      }

      // Clean up campaign based on ad type
      if (campaign.adtype === AD_TYPES.image.id) {
        campaign.textstring = null;
        campaign.textcss = null;
      }

      if (campaign.adtype === AD_TYPES.text.id) {
        campaign.filename = null;
        campaign.filesize = null;
        campaign.hash = null;
        campaign.metadata = null;
        campaign.mimetype = null;
        campaign.alttext = null;
        campaign.inline = null;
        campaign.is2x = null;
        campaign.isplaceholder = null;
        campaign.previewURL = '';
      }

      // Handle schedule
      if (startDate && endDate) {
        campaign.schedule = {
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString()
        };
      }

      if (!campaign.enableschedule) {
        campaign.schedule = null;
      }

      // Update targeting
      activeTargeting = await TargetingService.get();
      campaign = determineCampaignStatus(campaign, activeTargeting);

      const campaignTargeting = [...recipients, ...domains];

      const currentAccountTargeting = activeTargeting.accounts
        .filter(({ ads }) => ads.filter(({ text }) => text === campaign.rulename).length)
        .map(({ name }) => name);

      const findTargetingDiff = (originalArray, modifiedArray) => {
        const modifiedSet = new Set(modifiedArray);
        return originalArray.filter((element) => !modifiedSet.has(element));
      };

      const removedAccounts = findTargetingDiff(currentAccountTargeting, campaignTargeting);

      const updatedAccounts = activeTargeting.accounts.map((account) => {
        if (removedAccounts.length && removedAccounts.includes(account.name)) {
          account.ads = account.ads.filter(({ text }) => text !== campaign.rulename);
        }
        return account;
      });

      campaignTargeting.forEach((name) => {
        const existingAccount = updatedAccounts.find((account) => account.name === name);

        if (existingAccount) {
          const existingAd = existingAccount.ads.find(({ text }) => text === campaign.rulename);
          if (!existingAd) {
            existingAccount.ads.push({ text: campaign.rulename });
          }
        } else {
          updatedAccounts.push({
            name,
            ads: [{ text: campaign.rulename }],
            tags: [],
            isNew: true
          });
        }
      });

      const combinedTargeting = {
        accounts: updatedAccounts,
        departments: activeTargeting.departments
      };

      const targetingResponse = await TargetingService.update(combinedTargeting);

      if (targetingResponse?.status >= 400) {
        notify($format('label.FAILURE.UPDATE', { values: { type: campaign.rulename } }), 'error');
        throw new Error('Failed to update targeting');
      }

      const response = await CampaignsService.updateCampaign(campaign);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.UPDATE', { values: { type: campaign.rulename } }), 'error');
        throw new Error('Failed to update campaign');
      }

      notify($format('label.SUCCESS.UPDATE', { values: { item: campaign.rulename } }));

      step.data = {
        id: campaign.rulename,
        rollback: originalCampaign
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_CAMPAIGN'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackUpdate = async () => {
    $appStore.loading = true;

    try {
      if (!step.data?.rollback) {
        throw new Error('No rollback data available');
      }

      const rollbackData = step.data.rollback;

      const response = await CampaignsService.updateCampaign(rollbackData);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.ROLLBACK_CAMPAIGN'), 'error');
        throw new Error('Failed to rollback campaign');
      }

      notify($format('label.SUCCESS.ROLLBACK_CAMPAIGN'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_CAMPAIGN'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(() => {
    loadCampaigns();

    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: isStep0Disabled = !selectedCampaign || !campaign;

  $: hasUrl = campaign?.url?.length;

  $: hasChanges =
    campaign &&
    originalCampaign &&
    (JSON.stringify(campaign) !== JSON.stringify(originalCampaign) ||
      JSON.stringify(recipients) !== JSON.stringify(originalRecipients) ||
      JSON.stringify(domains) !== JSON.stringify(originalDomains)) &&
    !(campaign?.enableschedule && (!startDate || !endDate)) &&
    !(campaign?.linktype === LINK_TYPES.url.id && !hasUrl) &&
    !(campaign?.linktype === LINK_TYPES.url.id && validationError) &&
    !(campaign?.linktype === LINK_TYPES.doc.id && !selectedDocument) &&
    !(campaign?.linktype === LINK_TYPES.video.id && !selectedVideo) &&
    !(campaign?.adtype === AD_TYPES.image.id && !(files.length || campaign?.metadata?.origfile)) &&
    !(campaign?.adtype === AD_TYPES.text.id && !campaign.textstring);

  $: isStep1Disabled = !hasChanges;

  $: nextDisabled = currentStep !== 1 || isStep1Disabled;

  $: selectedCampaignSummary = selectedCampaign?.rulename || $format('label.NOT_SELECTED');

  $: formattedStartDate = formatDateString(startDate);
  $: formattedEndDate = formatDateString(endDate);
  $: dateInputValue = `${formattedStartDate} - ${formattedEndDate}`;
</script>

<div class="campaign-update-form">
  <Step
    active={currentStep === 0}
    disabled={false}
    title={$format('label.SELECT_CAMPAIGN')}
    summary={selectedCampaignSummary}
    onNext={currentStep === 0 ? handleNext : undefined}
    onStep={() => (currentStep = 0)}
    nextDisabled={isStep0Disabled}
  >
    <SectionItem label={$format('label.SELECT_CAMPAIGN')} description={$format('label.CHOOSE_CAMPAIGN_TO_UPDATE')}>
      {#if loading}
        <Spinner show inline />
      {:else}
        <Select
          items={campaigns}
          value={selectedCampaign}
          on:change={onSelectCampaign}
          placeholder={$format('label.PLACEHOLDER.SELECT_A_CAMPAIGN')}
          isClearable={false}
          showChevron
          searchable
        >
          <svelte:fragment slot="item" let:item>
            <div class="campaign-item">
              <div class="campaign-info">
                <b>{item.rulename}</b>
                <small>{item.adtype === 'image' ? $format('label.IMAGE_AD') : $format('label.TEXT_AD')}</small>
              </div>
            </div>
          </svelte:fragment>
        </Select>
      {/if}
    </SectionItem>

    {#if selectedCampaign}
      <SectionItem label={$format('label.PREVIEW')} description={$format('label.CURRENT_CAMPAIGN_PREVIEW')}>
        <div class="campaign-preview">
          {#if selectedCampaign.adtype === 'image'}
            {#if imageLoading}
              <div class="image-loading">
                <Spinner show inline />
              </div>
            {/if}
            <img
              src={selectedCampaign.previewURL}
              alt={selectedCampaign.rulename}
              loading="lazy"
              on:load={onImageLoad}
              on:error={onImageError}
              style={imageLoading ? 'display: none;' : ''}
            />
          {:else if selectedCampaign.adtype === 'text'}
            <div class="text-preview">
              {selectedCampaign.textstring}
            </div>
          {/if}
        </div>
      </SectionItem>
    {/if}
  </Step>

  <Step
    active={currentStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.EDIT_CAMPAIGN')}
    summary={campaign?.rulename || $format('label.NOT_CONFIGURED')}
    onBack={currentStep === 1 ? () => handleBack(1) : undefined}
    onStep={() => {
      if (currentStep >= 1) currentStep = 1;
    }}
    nextDisabled={isStep1Disabled}
  >
    {#if campaign}
      <div class="edit-form-wrapper">
        <EditForm
          bind:campaign
          bind:startDate
          bind:endDate
          bind:isOpen
          bind:defaultMonth
          bind:defaultYear
          bind:dateInputValue
          bind:tags
          bind:selectedTags
          bind:filterText
          bind:categories
          bind:selectedCategories
          bind:folders
          bind:selectedFolders
          bind:selectedLabels
          bind:documentItems
          bind:selectedDocument
          bind:videoItems
          bind:selectedVideo
          bind:files
          bind:validationError
          bind:errorMessage
          bind:recipients
          bind:domains
          {toggleDatePicker}
          {onDateInputChange}
          {onDatePickerChange}
          {onTagChange}
          {onTagClear}
          {onCategoryChange}
          {onSelect}
          {onClickAction}
          {onDocumentChange}
          {onVideoChange}
          {onValidation}
          {onUrlChange}
          {onParamsChange}
          {onAdType}
          {onFileUpload}
          {onRemoveFile}
        />
      </div>
    {/if}
  </Step>
</div>

<style lang="scss">
  .campaign-update-form {
    .campaign-item {
      .campaign-info {
        display: flex;
        flex-direction: column;
        gap: 2px;

        b {
          color: var(--color-gray-900);
        }

        small {
          color: var(--color-gray-600);
        }
      }
    }

    .campaign-preview {
      background-color: var(--color-white);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-base);
      display: flex;
      justify-content: center;
      padding: var(--padding-small);
      position: relative;

      img {
        display: block;
        height: auto;
        max-width: 100%;
        min-height: 40px;
      }

      .text-preview {
        padding: var(--padding-base);
      }

      .image-loading {
        min-height: 60px;
        padding: var(--padding-large);
      }
    }

    .edit-form-wrapper :global {
      .section {
        margin-bottom: var(--margin-large);

        h2 {
          font-size: var(--font-size-large);
          margin-bottom: var(--margin-base);
        }
      }
    }
  }
</style>
